import type { OnboardingAgreement, OnboardingTransitionResult } from '@doneverse/shared'
import { api } from '../api/index.js'

/**
 * Founder-triggerable onboarding transitions (ClickUp 86d3uknr2).
 *
 * There is no fetch here on purpose: onboarding state rides on `GET /v1/me`, so
 * the tracker reads the profile query the shell already holds rather than
 * adding a second request to every page load.
 */
export const onboardingService = {
  /**
   * Complete a step the founder is allowed to assert (4 = product tour done or
   * skipped, 9 = doer intro video watched). Anything else 403s server-side.
   */
  completeStep: async (
    step: number,
    metadata?: Record<string, unknown>,
  ): Promise<OnboardingTransitionResult> =>
    api
      .post(metadata ? { metadata } : {}, `/v1/onboarding/steps/${step}/complete`)
      .json<OnboardingTransitionResult>(),

  /**
   * The founder's service agreement for step 1 — created on the first call,
   * the same document on every one after.
   *
   * A POST that a query hook reads, which is unusual and deliberate: it creates
   * on first use but is idempotent, so treating it as a read is honest and
   * saves a second round trip to fetch what the create just returned.
   *
   * Null on a 503, meaning DocuSeal is not configured for this environment.
   * That is a deployment state, not a failure: the founder is being sent the
   * agreement by email instead and the Keap event still closes the step, so the
   * surface shows the waiting state rather than an error. Every other status
   * throws and the surface renders its retry.
   */
  agreement: async (): Promise<OnboardingAgreement | null> =>
    api
      .post({}, '/v1/onboarding/agreement')
      .error(503, () => null)
      .json<OnboardingAgreement>(),

  /**
   * Save one intake answer the browser collected itself (Onboarding 04).
   *
   * Only for the questions no client tool can wait on — the brand-asset upload
   * and the two grids. Everything else the founder answers travels back through
   * the agent, which records it; the API 403s those here.
   *
   * Throws on failure, deliberately. The caller must not tell the agent an
   * answer landed when it did not: the agent re-reads the session to pick it up,
   * would find the question still outstanding, and re-ask — which is the correct
   * recovery, but only if nobody claimed success in between.
   */
  saveIntakeAnswer: async (questionId: string, value: unknown): Promise<void> => {
    await api.post({ question_id: questionId, value }, '/v1/onboarding/intake/answers').res()
  },
}
